import React from 'react';
import { X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { PhoneInput } from './PhoneInput';

interface NewAdminUserModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

export function NewAdminUserModal({ onClose, onSuccess }: NewAdminUserModalProps) {
  const [form, setForm] = React.useState({
    name: '',
    whatsapp: '',
    password: '',
    tipo_usuario: 'cliente' as 'cliente' | 'admin'
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // Format WhatsApp number
      const digits = form.whatsapp.replace(/\D/g, '');
      if (digits.length !== 11) {
        throw new Error('O número deve ter 11 dígitos (DDD + número)');
      }

      // Create auth user
      const email = `55${digits}@whatsapp.user`;
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email,
        password: form.password,
        data: {
          name: form.name,
          whatsapp: digits,
        }
      });

      if (signUpError) throw signUpError;
      if (!authData.user) throw new Error('Erro ao criar usuário');

      // If user should be admin, add to admin_users table
      if (form.tipo_usuario === 'admin') {
        const { error: adminError } = await supabase
          .from('admin_users')
          .insert({
            id: authData.user.id
          });

        if (adminError) throw adminError;
      }

      // Add block status record
      const { error: blockError } = await supabase
        .from('user_block_status')
        .insert({
          user_id: authData.user.id,
          is_blocked: false
        });

      if (blockError) throw blockError;

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error creating admin user:', err);
      setError(err instanceof Error ? err.message : 'Erro ao criar usuário');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-gray-900">
            Novo Usuário
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-100 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Nome completo
            </label>
            <input
              required
              type="text"
              id="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="whatsapp" className="block text-sm font-medium text-gray-700 mb-1">
              WhatsApp
            </label>
            <PhoneInput
              id="whatsapp"
              value={form.whatsapp}
              onChange={(value) => setForm({ ...form, whatsapp: value })}
              placeholder="(11) 98765-4321"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
              Senha
            </label>
            <input
              required
              type="password"
              id="password"
              minLength={6}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
            <p className="mt-1 text-sm text-gray-500">
              Mínimo de 6 caracteres
            </p>
          </div>

          <div>
            <label htmlFor="tipo_usuario" className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Usuário
            </label>
            <select
              id="tipo_usuario"
              value={form.tipo_usuario}
              onChange={(e) => setForm({ ...form, tipo_usuario: e.target.value as 'cliente' | 'admin' })}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            >
              <option value="cliente">Cliente</option>
              <option value="admin">Administrador</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isSubmitting ? 'Criando...' : 'Criar Usuário'}
          </button>
        </form>
      </div>
    </div>
  );
}